import { UserType } from '@/types/user';
import { RecipientType } from '@/types/news';
import { getLanguage } from '@/utils/languageUtils';
import { 
  targetUserTypeLabels, 
  newsStatusLabels, 
  newsActionLabels, 
  newsMessageLabels, 
  newsUILabels,
  newsConfirmationLabels,
  newsErrorLabels,
  newsUITextLabels
} from '@/i18n/labels/newsLabels';

/**
 * Get targetUserType label and badge styling for display
 * Uses i18n labels from news.json
 */
export function getTargetUserTypeBadge(targetUserType: string) {
  const labels = targetUserTypeLabels(getLanguage());
  const label = labels[targetUserType] || targetUserType;
  
  switch (targetUserType) {
    case "all":
      return {
        label,
        className:
          "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600",
      };
    case "dealers":
      return {
        label,
        className:
          "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700",
      };
    case "business":
      return {
        label,
        className:
          "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-700",
      };
    case "individuals":
      return {
        label,
        className:
          "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700",
      };
    case "internally":
      return {
        label,
        className:
          "bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-700",
      };
    default:
      return {
        label,
        className:
          "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600",
      };
  }
}

/**
 * Get news status label for display
 * Uses i18n labels from news.json
 */
export function getNewsStatusLabel(visible: boolean): string {
  const labels = newsStatusLabels(getLanguage());
  return visible ? labels.published : labels.hidden;
}

/**
 * Get news action label for display
 * Uses i18n labels from news.json
 */
export function getNewsActionLabel(visible: boolean): string {
  const labels = newsActionLabels(getLanguage());
  return visible ? labels.unpublish : labels.publish;
}

/**
 * Get news status message for toast notifications based on the new status after an action
 * Uses i18n labels from news.json
 * @param isPublished - true if news is now published, false if now hidden
 */
export function getNewsStatusMessage(isPublished: boolean): string {
  const labels = newsMessageLabels(getLanguage());
  return isPublished ? labels.published : labels.hidden;
}

/**
 * Get news UI label (e.g. "Publicerad:", "Skapad:")
 * Uses i18n labels from news.json
 */
export function getNewsUILabel(key: 'published' | 'created'): string {
  const labels = newsUILabels(getLanguage());
  return labels[key] || key;
}

/**
 * Get display label for RecipientType (for NewsRecipientSelector buttons)
 */
export function getRecipientDisplayLabel(recipient: RecipientType): string {
  const labels = targetUserTypeLabels(getLanguage());
  const key = mapRecipientToTargetUserType(recipient);
  return labels[key] || recipient;
}

/**
 * Maps backend targetUserType string to frontend RecipientType
 * Backend: 'all', 'dealers', 'business', 'individuals', 'internally'
 * Frontend: 'all', 'dealers', 'businesses', 'private', 'internal'
 */
export function mapTargetUserTypeToRecipient(
  targetUserType: string
): RecipientType {
  switch (targetUserType) {
    case "individuals":
      return "private";
    case "internally":
      return "internal";
    case "business":
      return "businesses";
    case "dealers":
      return "dealers";
    case "all":
      return "all";
    default:
      return "all";
  }
}

/**
 * Get news confirmation message
 * Uses i18n labels from news.json
 */
export function getNewsConfirmationLabel(key: 'deleteNews' | 'togglePublish', action?: string): string {
  const labels = newsConfirmationLabels(getLanguage());
  let message = labels[key] || '';
  if (key === 'togglePublish' && action) {
    message = message.replace('{action}', action);
  }
  return message;
}

/**
 * Get news error message
 * Uses i18n labels from news.json
 */
export function getNewsErrorLabel(
  key:
    | 'selectRecipient'
    | 'mustBeLoggedIn'
    | 'mustBeLoggedInToCreate'
    | 'titleAndContentRequired'
): string {
  const labels = newsErrorLabels(getLanguage());
  return labels[key] || key;
}

/**
 * Get news UI text label
 * Uses i18n labels from news.json
 */
export function getNewsUITextLabel(key: string): string {
  const labels = newsUITextLabels(getLanguage());
  return labels[key] || key;
}

/**
 * Get news toast message
 * Uses i18n labels from news.json
 */
export function getNewsToastMessage(key: string): string {
  const labels = newsMessageLabels(getLanguage());
  return labels[key] || key;
}

/**
 * Maps frontend RecipientType to backend targetUserType string
 * Frontend: 'all', 'dealers', 'businesses', 'private', 'internal'
 * Backend: 'all', 'dealers', 'business', 'individuals', 'internally'
 */
/** Maps a single selected recipient chip to admin user list role filter */
export function resolveUserTypeFilterFromRecipients(
  selected: RecipientType[]
): string | undefined {
  if (selected.includes("all") || selected.length !== 1) return undefined;
  switch (selected[0]) {
    case "dealers":
      return "dealer";
    case "businesses":
      return "business";
    case "private":
      return "individual";
    case "internal":
      return "admin";
    default:
      return undefined;
  }
}

export function mapRecipientToTargetUserType(
  recipient: RecipientType
): "all" | "dealers" | "business" | "individuals" | "internally" {
  switch (recipient) {
    case "all":
      return "all";
    case "dealers":
      return "dealers";
    case "businesses":
      return "business";
    case "private":
      return "individuals";
    case "internal":
      return "internally";
    default:
      return "all";
  }
}

/**
 * Maps RecipientType to NewsItem targetUserType (same as mapRecipientToTargetUserType)
 * Alias for consistency with existing code
 */
export function mapRecipientType(
  type: RecipientType
): "all" | "dealers" | "business" | "individuals" | "internally" {
  return mapRecipientToTargetUserType(type);
}

/**
 * Maps frontend UserType enum to backend targetUserType string
 * Backend expects: 'all', 'dealers', 'business', 'individuals', 'internally'
 */
export function mapUserTypeToTargetUserType(userType: UserType | string | number): string | undefined {
  // Handle string/number inputs (from user.type which might be converted to string)
  if (typeof userType === 'string') {
    // If it's already a valid backend value, return it
    const validBackendValues = ['all', 'dealers', 'business', 'individuals', 'internally'];
    if (validBackendValues.includes(userType.toLowerCase())) {
      return userType.toLowerCase();
    }
    
    // Map string values
    switch (userType.toLowerCase()) {
      case 'admin':
        return 'all'; // Admin sees all news
      case 'dealer':
        return 'dealers';
      case 'business':
        return 'business';
      case 'individual':
      case 'private':
        return 'individuals';
      case 'internal':
      case 'internally':
        return 'internally';
      default:
        return 'all'; // Default to all if unknown
    }
  }
  
  // Handle numeric enum values
  if (typeof userType === 'number') {
    switch (userType) {
      case UserType.Admin:
        return 'all'; // Admin sees all news
      case UserType.Dealer:
        return 'dealers';
      case UserType.Business:
        return 'business';
      case UserType.Individual:
        return 'individuals';
      default:
        return 'all';
    }
  }
  
  // Handle UserType enum directly
  switch (userType) {
    case UserType.Admin:
      return 'all'; // Admin sees all news
    case UserType.Dealer:
      return 'dealers';
    case UserType.Business:
      return 'business';
    case UserType.Individual:
      return 'individuals';
    default:
      return 'all';
  }
}

